import { Monitor, RefreshCw } from "lucide-react";

import { Badge } from "@components/ui/badge";
import { Button } from "@components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@components/ui/tabs";

import type { CallAudioShare } from "../hooks/useCallAudioShare";
import { useMicDeviceOptions } from "../hooks/useMicDeviceOptions";
import { useMicPreview } from "../hooks/useMicPreview";
import { useThemeColor } from "../hooks/useThemeColor";
import type { SessionMode } from "../types/sessionMode";
import { TrackWaveform } from "./TrackWaveform";

type TelehealthAudioSetupProps = {
  mode: SessionMode;
  onModeChange: (mode: SessionMode) => void;
  micDeviceId: string | undefined;
  onMicDeviceChange: (deviceId: string | undefined) => void;
  /** The shared call-tab audio (MDRS-268), owned by the root RecordingHost. */
  callAudioShare: CallAudioShare;
  onToggleShare: () => void;
  shareError?: string | null;
  disabled?: boolean;
};

/**
 * Step 1 of the two-step consent flow (MDRS-303/MDRS-268, "Audio Setup").
 * Pill-tab In person/Telehealth switch, a live mic picker + level meter, and
 * — telehealth only — a "Share the call tab" gate that must succeed before
 * Continue is enabled (AudioRecorder owns that gating logic; this component
 * only renders state and reports intent upward).
 */
export const TelehealthAudioSetup = ({
  mode,
  onModeChange,
  micDeviceId,
  onMicDeviceChange,
  callAudioShare,
  onToggleShare,
  shareError,
  disabled = false,
}: TelehealthAudioSetupProps) => {
  const isTelehealth = mode === "telehealth";
  const isCallShared = callAudioShare.status === "sharing";
  const micOptions = useMicDeviceOptions();
  const { track: micTrack, status: micStatus } = useMicPreview(micDeviceId);
  // Same success-green used for the live Scribe session's "You" source card
  // waveform (ScribeSession.tsx), so the mic meter reads as one consistent
  // component across both the Audio Setup step and the live session.
  const micWaveColor = useThemeColor("--success-600", "hsl(142 71% 35%)");

  return (
    <div className="space-y-4">
      <Tabs value={mode} onValueChange={(val) => onModeChange(val as SessionMode)}>
        <TabsList variant="pill" className="grid w-full grid-cols-2">
          <TabsTrigger value="in_person" variant="pill" disabled={disabled}>
            In person
          </TabsTrigger>
          <TabsTrigger value="telehealth" variant="pill" disabled={disabled}>
            Telehealth
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="space-y-3 rounded-lg border border-border p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">
              {isTelehealth ? "Your microphone" : "Room microphone"}
            </p>
            <p className="text-[11.5px] text-foreground-muted">
              {isTelehealth
                ? "Captures you in the room"
                : "Captures everyone in the room"}
            </p>
          </div>
          <Badge variant={micStatus === "error" ? "destructive" : "success"}>
            {micStatus === "error" ? "Unavailable" : "Ready"}
          </Badge>
        </div>
        <div className="flex items-center gap-2.5">
          <Select
            value={micDeviceId ?? "default"}
            onValueChange={(val) =>
              onMicDeviceChange(val === "default" ? undefined : val)
            }
            disabled={disabled}
          >
            <SelectTrigger
              className="flex-1"
              aria-label={isTelehealth ? "Your microphone" : "Room microphone"}
            >
              <SelectValue placeholder="Please select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="default">Default microphone</SelectItem>
              {micOptions.map((mic) => (
                <SelectItem key={mic.deviceId} value={mic.deviceId}>
                  {mic.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {micStatus === "ready" && (
            <TrackWaveform track={micTrack} color={micWaveColor} />
          )}
        </div>
      </div>

      {isTelehealth && (
        <div className="space-y-3 rounded-lg border border-border p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">
                Everyone on the call
              </p>
              <p className="text-[11.5px] text-foreground-muted">
                {isCallShared ? "Tab audio shared" : "Not shared yet"}
              </p>
            </div>
            <Badge variant={isCallShared ? "success" : "warning"}>
              {isCallShared ? "Connected" : "Needs sharing"}
            </Badge>
          </div>
          <Button
            type="button"
            variant={isCallShared ? "simple-outline" : "default"}
            className="w-full"
            onClick={onToggleShare}
            disabled={disabled}
          >
            {isCallShared ? (
              <>
                <RefreshCw className="mr-1.5 size-3.5" />
                Change shared tab
              </>
            ) : (
              <>
                <Monitor className="mr-1.5 size-3.5" />
                Share the call tab
              </>
            )}
          </Button>
          {shareError && <p className="text-xs text-destructive">{shareError}</p>}
          <p className="text-[11.5px] leading-[17px] text-foreground-muted">
            You&apos;ll be asked to pick a source to share. Choose the browser tab
            running the video call, not a window and not your whole screen. Supported
            in Chrome and Edge; other browsers fall back to microphone only.
          </p>
        </div>
      )}
    </div>
  );
};
